import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { up } from 'styled-breakpoints';
import { TextLink, P2, H5, Modal } from 'pfm-ui-components';

import * as constants from '../../../constants/Footer';
import DisclaimerModalInfo from './DisclaimerModal';
import PrivacyModalInfo from './PrivacyPolicyModal';
import Container from '../Container';
import { pxToRem } from '../../../styles/utils';
import { featureToggle } from '../../../constants/common';

export const footerHeightMobile = 140;
export const footerHeightWeb = 100;

interface FooterProps {
  hideDisclaimer?: boolean; // To display detailed disclaimer message or not for some pages
  showDisclaimerModal?: boolean; // To open/close disclaimer modal from parent. see scheme detail page
  setShowDisclaimerModal?: Function; // To open/close disclaimer modal from parent. see scheme detail page
  hasNavigationBar?: boolean; // whether a particular page has navigation bar or not for mobile
}

/**
 * @author
 * @function Footer
 **/

const FooterWrapper = styled.div<{ hasNavigationBar: boolean }>`
  background-color: ${(props) => props.theme.palette.grey5};
  position: absolute;
  bottom: 0;
  width: 100%;
  min-width: ${pxToRem(360)};
  /* height: ${pxToRem(footerHeightMobile)}; */
  height: fit-content;
  ${up('md')} {
    /* height: ${pxToRem(footerHeightWeb)}; */
    margin-bottom: 0;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: ${pxToRem(24)};
  margin-bottom: ${(props) => (props.hasNavigationBar ? pxToRem(56) : '0')};
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${pxToRem(15)} 0 ${pxToRem(20)} 0;
`;

const FooterLinksContainer = styled(FooterContainer)`
  padding: 0;
  flex-direction: row;
`;

const FooterDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${up('md')} {
    flex-direction: row;
  }
`;

// Wrapper for the data elements of the footer component
const FooterData = styled.div<{ hideDisclaimer: boolean }>`
  display: block;
  margin-right: ${pxToRem(8)};
  padding-top: ${(props) => (props.hideDisclaimer ? '12px' : '16px')};
  ${up('md')} {
    margin-right: ${pxToRem(20)};
  }
`;

const ModalHeading = styled(H5)`
  color: ${(props) => props.theme.palette.black};
`;

const StyledTextLink = styled(TextLink)`
  font-size: ${pxToRem(12)};
  cursor: pointer;
  ${up('md')} {
    font-size: ${pxToRem(14)};
  }
`;

const ModalMessageContainer = styled.div`
  max-height: 60%;
  height: ${pxToRem(329)};
  overflow: auto;
  /* padding: ${pxToRem(16)}; */
`;

export default function Footer(props: FooterProps) {
  const { showDisclaimerModal, setShowDisclaimerModal, hasNavigationBar } = props;
  const [isDisclaimerOpen, setDisclaimerIsOpen] = useState(false); // To open/close Disclaimer Modal
  const closeDisclaimerModal = () => {
    setShowDisclaimerModal && setShowDisclaimerModal(false);
    setDisclaimerIsOpen(false);
  };
  const openDisclaimerModal = () => setDisclaimerIsOpen(true);

  const [isPrivacyPolicyOpen, setPrivacyPolicyIsOpen] = useState(false); // To open/close Privacy Policy Modal
  const closePrivacyPolicyModal = () => setPrivacyPolicyIsOpen(false);
  const openPrivacyPolicyModal = () => setPrivacyPolicyIsOpen(true);

  const DisclaimerHeader = () => <ModalHeading>{constants.DISCLAIMER}</ModalHeading>;
  const PrivacyPolicyHeader = () => <ModalHeading>{constants.PRIVACY_POLICY}</ModalHeading>;

  useEffect(() => {
    // to update parent state on close if any
    setDisclaimerIsOpen(!!showDisclaimerModal);
  }, [showDisclaimerModal]);

  return (
    <FooterWrapper hasNavigationBar={hasNavigationBar}>
      <Container>
        <FooterContainer>
          {/* Based on the props passed for hiding/showing detailed disclaimer show the disclaimer message */}
          {!props.hideDisclaimer ? (
            <FooterDataContainer>
              <P2>{constants.FOOTER_MESSAGE}</P2>
            </FooterDataContainer>
          ) : null}

          {/* Container that will hold data like Copyright information and 3 Links that will always be present despite
      of which device user is using from */}
          <FooterDataContainer>
            <FooterData hideDisclaimer={props.hideDisclaimer}>
              <P2>{constants.COPYRIGHT_MESSAGE}</P2>
            </FooterData>
            {/* Container to store the 3 Links for Disclaimer, Privacy Policy and Code of Commitment */}
            <FooterLinksContainer>
              <FooterData hideDisclaimer={props.hideDisclaimer}>
                <StyledTextLink onClick={openDisclaimerModal}>{constants.DISCLAIMER}</StyledTextLink>
              </FooterData>
              {/* Show Disclaimer Modal when clicked on Disclaimer Text */}
              {isDisclaimerOpen && (
                <Modal onClose={closeDisclaimerModal} HeaderComponent={DisclaimerHeader()} className="nominee-modal">
                  <ModalMessageContainer>
                    <DisclaimerModalInfo />
                  </ModalMessageContainer>
                </Modal>
              )}
              <FooterData hideDisclaimer={props.hideDisclaimer}>
                <StyledTextLink>{constants.LINK_SEPARATOR}</StyledTextLink>
              </FooterData>
              <FooterData hideDisclaimer={props.hideDisclaimer}>
                <StyledTextLink onClick={openPrivacyPolicyModal}>{constants.PRIVACY_POLICY}</StyledTextLink>
              </FooterData>
              {/* Open Privacy Policy Modal when clicked on the Text */}
              {isPrivacyPolicyOpen && (
                <Modal
                  onClose={closePrivacyPolicyModal}
                  HeaderComponent={PrivacyPolicyHeader()}
                  className="nominee-modal"
                >
                  <ModalMessageContainer>
                    <PrivacyModalInfo />
                  </ModalMessageContainer>
                </Modal>
              )}
              <FooterData hideDisclaimer={props.hideDisclaimer}>
                <StyledTextLink>{constants.LINK_SEPARATOR}</StyledTextLink>
              </FooterData>
              {/* Redirect to a different page on click of the link. It GETS URL from the props */}
              <FooterData hideDisclaimer={props.hideDisclaimer}>
                <StyledTextLink
                  href="https://www.axisbank.com/webforms/code-of-commitment.aspx"
                  target="_blank"
                  tabIndex={0}
                >
                  {constants.CODE_OF_COMMITMENT}
                </StyledTextLink>
              </FooterData>
            </FooterLinksContainer>
            {/* Container to store 3 links ends here */}
          </FooterDataContainer>
          {/* {featureToggle.BETA_FOOTER_DESCRIPTION && (
            <FooterData hideDisclaimer={props.hideDisclaimer}>
              <P2>{constants.BETA_FOOTER_DISCLAIMER}</P2>
            </FooterData>
          )} */}
        </FooterContainer>
      </Container>
    </FooterWrapper>
  );
}

Footer.defaultProps = {
  hideDisclaimer: false,
  hasNavigationBar: false,
};
